import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "flex flex-col" };
const _hoisted_5 = { class: "flex flex-col gap-4" };
const _hoisted_6 = { class: "text-base font-bold text-(--text-text-heading) leading-6" };
const _hoisted_7 = { class: "flex flex-col gap-4" };
const _hoisted_8 = { class: "flex flex-col gap-4 mt-8" };
const _hoisted_9 = { class: "text-base font-bold text-(--text-text-heading) leading-6" };
const _hoisted_10 = { class: "flex flex-wrap gap-5 items-start" };
const _hoisted_11 = { class: "flex-1 min-w-[255px]" };
const _hoisted_12 = { class: "flex-1 min-w-[255px]" };
const _hoisted_13 = { class: "flex flex-col gap-4 mt-[14px] mb-[-18px]" };
const _hoisted_14 = { class: "flex flex-col gap-0.5" };
const _hoisted_15 = { class: "text-base font-bold text-(--text-text-heading) leading-6" };
const _hoisted_16 = { class: "text-xs text-(--text-text-body-subtle)" };
const _hoisted_17 = { class: "flex flex-wrap gap-x-5 gap-y-[2px] items-start" };
const _hoisted_18 = { class: "flex-1 min-w-[255px]" };
const _hoisted_19 = { class: "flex-1 min-w-[255px]" };
const _hoisted_20 = { class: "w-full" };
import { computed, ref, watch, useId } from "vue";
import { useI18n } from "vue-i18n";
import { Alert, Button, Modal, PhoneInput, SelectInput, TextInput } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useAppToast } from "@/shared/composables/useAppToast";
import { updateHousehold } from "../../infrastructure/api/householdApi";
import { getContactById } from "../../infrastructure/adapters/contactService";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import HouseholdMemberRow from "./HouseholdMemberRow.vue";
// ─── Props / emits ─────────────────────────────────────────────────────────────
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditHouseholdModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        household: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["updated"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ──────────────────────────────────────────────────────────────
        const { t, locale } = useI18n();
        const { canEditOphub: canEdit } = usePermissions();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        // ─── Member rows ──────────────────────────────────────────────────────────────
        function buildRows() {
            return props.household.members.map((m) => ({
                _id: crypto.randomUUID(),
                contactId: m.contactId,
                contactLabel: m.fullName,
                email: m.email,
                role: m.role,
                isPrincipal: m.isPrincipal,
                isCurrentContact: m.contactId === props.contactId,
                householdMemberId: m.id,
            }));
        }
        function emptyRow() {
            return {
                _id: crypto.randomUUID(),
                contactId: null,
                contactLabel: "",
                email: null,
                role: null,
                isPrincipal: false,
                isCurrentContact: false,
            };
        }
        const memberRows = ref([]);
        function addRow() {
            memberRows.value.push(emptyRow());
        }
        function removeRow(index) {
            memberRows.value.splice(index, 1);
            // No principal auto-assign — user must designate a new principal explicitly
        }
        function updateRole(index, role) {
            memberRows.value[index] = { ...memberRows.value[index], role };
        }
        function updateContact(index, contactId, label, email) {
            memberRows.value[index] = { ...memberRows.value[index], contactId, contactLabel: label, email };
        }
        function setPrincipal(index) {
            memberRows.value = memberRows.value.map((row, i) => ({
                ...row,
                isPrincipal: i === index,
            }));
        }
        // ─── Foyer detail ─────────────────────────────────────────────────────────────
        const householdType = ref("");
        const greetingFormula = ref("");
        const typeOptions = computed(() => [
            { value: "Family", label: t("contact.detail.relations.foyer.card.type.Family") },
            { value: "SharedHousing", label: t("contact.detail.relations.foyer.card.type.SharedHousing") },
            { value: "Other", label: t("contact.detail.relations.foyer.card.type.Other") },
        ]);
        // ─── Communication section (read-only, from current principal) ────────────────
        const principalEmail = ref(null);
        const principalPhone = ref(null);
        const principalAddress = ref(null);
        async function fetchPrincipalDetails(contactId) {
            try {
                const detail = await getContactById(contactId);
                principalEmail.value =
                    detail.communicationChannels.find((c) => c.type === "Email" && c.isPrimary)?.value ?? null;
                principalPhone.value =
                    detail.communicationChannels.find((c) => c.type === "Phone" && c.isPrimary)?.value ?? null;
                const addr = detail.addresses.find((a) => a.isPrimary) ?? detail.addresses[0] ?? null;
                principalAddress.value =
                    addr?.label ?? (addr?.city ? [addr.line1, addr.city].filter(Boolean).join(", ") : null);
            }
            catch (err) {
                logger.error("EditHouseholdModal: failed to fetch principal contact details", err);
            }
        }
        const principalContactId = computed(() => memberRows.value.find((r) => r.isPrincipal)?.contactId ?? null);
        const principalName = computed(() => memberRows.value.find((r) => r.isPrincipal)?.contactLabel ?? "");
        // Handles principal changes made by the user during the modal session.
        watch(principalContactId, async (newId, oldId) => {
            if (newId === oldId)
                return;
            if (!newId) {
                principalEmail.value = null;
                principalPhone.value = null;
                principalAddress.value = null;
                return;
            }
            await fetchPrincipalDetails(newId);
        });
        // Re-initialise every time the modal opens so cancelled edits don't persist.
        watch(open, async (isOpen) => {
            if (!isOpen)
                return;
            principalEmail.value = null;
            principalPhone.value = null;
            principalAddress.value = null;
            memberRows.value = buildRows();
            householdType.value = props.household.type;
            greetingFormula.value = props.household.greetingFormula ?? "";
            const principalRow = memberRows.value.find((r) => r.isPrincipal);
            if (principalRow?.contactId) {
                await fetchPrincipalDetails(principalRow.contactId);
            }
        });
        // ─── Save gating ──────────────────────────────────────────────────────────────
        const hasDuplicateMembers = computed(() => {
            const ids = memberRows.value.map((r) => r.contactId).filter(Boolean);
            return ids.length !== new Set(ids).size;
        });
        const canSave = computed(() => {
            if (!canEdit.value)
                return false;
            if (memberRows.value.length < 2)
                return false;
            if (!memberRows.value.some((r) => r.isPrincipal))
                return false;
            if (hasDuplicateMembers.value)
                return false;
            if (!memberRows.value.every((r) => r.contactId && r.role))
                return false;
            if (memberRows.value.some((r) => r.contactId && !r.email))
                return false;
            return true;
        });
        // ─── Error codes ──────────────────────────────────────────────────────────────
        // Backend validation error codes the user can recover from → localized i18n keys.
        const HOUSEHOLD_ERROR_KEYS = {
            "Household.HouseholdType.Invalid": "contact.detail.relations.foyer.errors.invalidType",
            "Household.Members.MinTwoRequired": "contact.detail.relations.foyer.errors.minMembers",
            "Household.Member.AlreadyInActiveHousehold": "contact.detail.relations.foyer.errors.contactAlreadyInHousehold",
            "Household.Members.DuplicateContact": "contact.detail.relations.foyer.errors.duplicateMember",
            // OP-7457: the reference email (read-only here, sourced from the principal's primary channel)
            // can carry a blocked TLD (user@gmail.test) that OPHUB's B2C validator refuses — the explicit
            // message tells the user the principal's email is the problem, not the household edit itself.
            "Household.Email.Invalid": "contact.detail.relations.foyer.errors.emailInvalid",
        };
        // ─── Submit ───────────────────────────────────────────────────────────────────
        const saving = ref(false);
        async function handleSubmit() {
            if (!canSave.value)
                return;
            saving.value = true;
            try {
                await updateHousehold(props.household.id, props.household.members, {
                    name: principalName.value,
                    householdType: householdType.value || "Other",
                    greetingFormula: greetingFormula.value.trim() || null,
                    principalEmail: principalEmail.value,
                    members: memberRows.value.map((row) => ({
                        ...(row.householdMemberId ? { id: row.householdMemberId } : {}),
                        contactId: row.contactId,
                        role: row.role,
                        isPrincipal: row.isPrincipal,
                    })),
                });
                toastSuccess("contact.detail.relations.foyer.edit.success");
                emit("updated");
                open.value = false;
            }
            catch (err) {
                const code = getApiErrorCode(err);
                const mappedKey = code ? HOUSEHOLD_ERROR_KEYS[code] : undefined;
                if (!mappedKey) {
                    logger.error("EditHouseholdModal: save failed", err);
                }
                toastError(mappedKey ?? "contact.detail.relations.foyer.edit.error");
                // Reload household state even on failure — reconciliation is non-atomic so the
                // persisted state may differ from what the user sees in the modal.
                emit("updated");
                open.value = false;
            }
            finally {
                saving.value = false;
            }
        }
        // ─── Cancel ───────────────────────────────────────────────────────────────────
        function handleCancel() {
            open.value = false;
            // rows are re-initialised on next open via the watch
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                    _cache[3] || (_cache[3] = (v) => !v && handleCancel())
                ],
                size: "lg",
                "header-position": "center",
                "footer-position": "split",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(descriptionId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("h2", {
                            id: _unref(titleId),
                            class: "text-[length:var(--size-xxl)] font-[var(--weight-bold)]"
                        }, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.edit.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                        _createElementVNode("p", {
                            id: _unref(descriptionId),
                            class: "text-[length:var(--size-l)] text-(--text-text-body-subtle)"
                        }, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.edit.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        onClick: handleCancel
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('common.save'),
                        loading: saving.value,
                        disabled: !canSave.value,
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("h3", _hoisted_6, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.create.sections.members")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_7, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(memberRows.value, (row, index) => {
                                    return (_openBlock(), _createBlock(HouseholdMemberRow, {
                                        key: row._id,
                                        row: row,
                                        position: index + 1,
                                        "can-remove": memberRows.value.length > 1 && !row.isCurrentContact,
                                        "onUpdate:role": (role) => updateRole(index, role),
                                        "onUpdate:contact": (id, label, email) => updateContact(index, id, label, email),
                                        onTogglePrincipal: ($event) => (setPrincipal(index)),
                                        onRemove: ($event) => (removeRow(index))
                                    }, null, 8 /* PROPS */, ["row", "position", "can-remove", "onUpdate:role", "onUpdate:contact", "onTogglePrincipal", "onRemove"]));
                                }), 128 /* KEYED_FRAGMENT */))
                            ]),
                            _createVNode(_unref(Button), {
                                variant: "secondary",
                                size: "sm",
                                "icon-left": "ri-add-line",
                                label: _ctx.$t('contact.detail.relations.foyer.create.addMember'),
                                class: "self-start",
                                onClick: addRow
                            }, null, 8 /* PROPS */, ["label"]),
                            (memberRows.value.length < 2)
                                ? (_openBlock(), _createBlock(_unref(Alert), {
                                    key: 0,
                                    color: "danger",
                                    heading: _ctx.$t('contact.detail.relations.foyer.errors.minMembers')
                                }, null, 8 /* PROPS */, ["heading"]))
                                : (!memberRows.value.some((r) => r.isPrincipal))
                                    ? (_openBlock(), _createBlock(_unref(Alert), {
                                        key: 1,
                                        color: "danger",
                                        heading: _ctx.$t('contact.detail.relations.foyer.errors.principalRequired')
                                    }, null, 8 /* PROPS */, ["heading"]))
                                    : (hasDuplicateMembers.value)
                                        ? (_openBlock(), _createBlock(_unref(Alert), {
                                            key: 2,
                                            color: "danger",
                                            heading: _ctx.$t('contact.detail.relations.foyer.errors.duplicateMember')
                                        }, null, 8 /* PROPS */, ["heading"]))
                                        : _createCommentVNode("v-if", true)
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.create.sections.detail")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_10, [
                                _createElementVNode("div", _hoisted_11, [
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: householdType.value,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((householdType).value = $event)),
                                        label: _ctx.$t('contact.detail.relations.foyer.create.fields.type'),
                                        placeholder: _ctx.$t('contact.detail.relations.foyer.create.fields.typePlaceholder'),
                                        options: typeOptions.value,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options"])
                                ]),
                                _createElementVNode("div", _hoisted_12, [
                                    _createVNode(_unref(TextInput), {
                                        modelValue: greetingFormula.value,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((greetingFormula).value = $event)),
                                        label: _ctx.$t('contact.detail.relations.foyer.create.fields.politesse'),
                                        placeholder: _ctx.$t('contact.detail.relations.foyer.create.fields.politessePlaceholder'),
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder"])
                                ])
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                                _createElementVNode("h3", _hoisted_15, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.create.sections.communication")), 1 /* TEXT */),
                                _createElementVNode("p", _hoisted_16, _toDisplayString(_ctx.$t("contact.detail.relations.foyer.create.sections.communicationHint", { name: principalName.value })), 1 /* TEXT */)
                            ]),
                            _createElementVNode("div", _hoisted_17, [
                                _createElementVNode("div", _hoisted_18, [
                                    _createVNode(_unref(TextInput), {
                                        "model-value": principalEmail.value ?? '',
                                        label: _ctx.$t('contact.detail.relations.foyer.create.fields.email'),
                                        disabled: true,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["model-value", "label"])
                                ]),
                                _createElementVNode("div", _hoisted_19, [
                                    _createVNode(_unref(PhoneInput), {
                                        "model-value": principalPhone.value ?? '',
                                        label: _ctx.$t('contact.detail.relations.foyer.create.fields.phone'),
                                        "country-list-label": _ctx.$t('common.country'),
                                        locale: _unref(locale),
                                        disabled: true,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["model-value", "label", "country-list-label", "locale"])
                                ]),
                                _createElementVNode("div", _hoisted_20, [
                                    _createVNode(_unref(TextInput), {
                                        "model-value": principalAddress.value ?? '',
                                        label: _ctx.$t('contact.detail.relations.foyer.create.fields.address'),
                                        "icon-left": "ri-map-pin-2-line",
                                        disabled: true,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["model-value", "label"])
                                ])
                            ])
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]));
        };
    }
});
